import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../config/msalConfig";
import { clearSessionCache } from "../services/sessionCache";
import { logger } from "../services/logger.service";

// localStorage keys for user preferences persistence
const INSTANCE_STORAGE_KEY = "sky-selected-instance-id";
const TENANT_STORAGE_KEY = "sky-selected-tenant-id";
const THEME_STORAGE_KEY = "sky-user-theme";
const LANGUAGE_STORAGE_KEY = "sky-user-language";

/**
 * Batched localStorage write operation
 * Performance optimization: Combines multiple localStorage writes into a single operation
 * This reduces the overhead of individual setItem calls (~50ms savings for 3+ writes)
 *
 * @param items - Object with key-value pairs to write to localStorage
 */
export function batchSetStorage(items: Record<string, string>): void {
  const entries = Object.entries(items);
  for (const [key, value] of entries) {
    if (value !== undefined && value !== null) {
      localStorage.setItem(key, value);
    }
  }
}

/**
 * Batched localStorage read operation
 * @param keys - Array of keys to read from localStorage
 * @returns Object with key-value pairs from localStorage
 */
export function batchGetStorage(keys: string[]): Record<string, string | null> {
  const result: Record<string, string | null> = {};
  for (const key of keys) {
    result[key] = localStorage.getItem(key);
  }
  return result;
}

/**
 * Auth Service for non-React contexts (utilities, services, etc.)
 *
 * This service provides authentication functionality without React hooks,
 * making it usable in utility files, service files, and other non-component code.
 *
 * Usage:
 *   import { authService } from "../utils/authService";
 *   const token = await authService.getAccessToken();
 */

let msalInstance: PublicClientApplication | null = null;

// Token cache to avoid repeated acquireTokenSilent calls
interface CachedToken {
  accessToken: string;
  expiresAt: number; // timestamp in ms
}
let cachedToken: CachedToken | null = null;
const TOKEN_BUFFER_MS = 60000; // Refresh token 1 minute before expiry

// Shared promise for an acquisition already in progress. The cache above is only
// populated AFTER acquireTokenSilent resolves, so without this a cold cache (every
// fresh login) lets concurrent callers race into acquireTokenSilent — and if silent
// acquisition fails they would each fire acquireTokenRedirect, producing
// "interaction_in_progress" and a stuck login.
let inFlightTokenRequest: Promise<string> | null = null;

/**
 * Set the MSAL instance (called from index.tsx during initialization)
 */
export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance;
}

/**
 * Clear the cached token (call on logout or when token becomes invalid)
 */
export function clearCachedToken(): void {
  cachedToken = null;
  inFlightTokenRequest = null;
}

/**
 * Get the MSAL instance
 */
function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    throw new Error("MSAL instance not initialized. Call setMsalInstance first.");
  }
  return msalInstance;
}

/**
 * Check if user is logged in
 */
export function isLoggedIn(): boolean {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();
  return accounts.length > 0;
}

/**
 * Get access token with caching to avoid repeated acquireTokenSilent calls
 *
 * Performance optimization: Caches the token and only calls acquireTokenSilent
 * when the cached token is expired or about to expire (within 1 minute).
 * This eliminates 400-600ms delays per API call during initialization.
 */
export async function getAccessToken(): Promise<string> {
  // Check if we have a valid cached token
  if (cachedToken && Date.now() < cachedToken.expiresAt - TOKEN_BUFFER_MS) {
    return cachedToken.accessToken;
  }

  // Concurrent callers share a single acquisition — see inFlightTokenRequest above.
  if (inFlightTokenRequest) {
    return inFlightTokenRequest;
  }

  inFlightTokenRequest = acquireAccessToken().finally(() => {
    inFlightTokenRequest = null;
  });

  return inFlightTokenRequest;
}

/**
 * Does this silent failure need the user to sign in again? Only then is a redirect worth it.
 * B2C reports a dead refresh token from the upstream identity provider (AADB2C90289) as a
 * server error carrying invalid_grant, not as interaction-required, so that counts too.
 * A timeout or a dropped network call does not: redirecting for those sent a user who had just
 * refreshed the page back through the whole Microsoft login.
 */
export function needsInteractiveSignIn(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  const e = error as { errorCode?: string; errorMessage?: string; message?: string } | null;
  const text = `${e?.errorCode ?? ""} ${e?.errorMessage ?? ""} ${e?.message ?? ""}`;
  return /invalid_grant|interaction_required|login_required|consent_required/.test(text);
}

function cacheResponse(response: { accessToken: string; expiresOn: Date | null }): void {
  if (response.expiresOn) {
    cachedToken = {
      accessToken: response.accessToken,
      expiresAt: response.expiresOn.getTime(),
    };
  }
}

async function acquireAccessToken(): Promise<string> {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error("No authenticated user found");
  }

  const silentRequest = { ...loginRequest, account: accounts[0] };
  let failure: unknown;

  try {
    const response = await instance.acquireTokenSilent(silentRequest);
    cacheResponse(response);
    return response.accessToken;
  } catch (error: unknown) {
    cachedToken = null;
    failure = error;

    const msalError = error as { errorCode?: string };
    if (msalError?.errorCode === "no_token_request_cache_error") {
      logger.warn("Token request cache error detected, clearing cache and retrying");
      // Clear any stale cache entries
      sessionStorage.clear();
    }
  }

  // A passing failure (iframe timeout, network blip): try once more before giving up.
  if (!needsInteractiveSignIn(failure)) {
    try {
      const response = await instance.acquireTokenSilent(silentRequest);
      cacheResponse(response);
      return response.accessToken;
    } catch (retryError: unknown) {
      failure = retryError;
    }
  }

  if (!needsInteractiveSignIn(failure)) {
    logger.error("Silent token acquisition failed", failure);
    throw failure instanceof Error
      ? failure
      : new Error("Failed to acquire token. Please try again.");
  }

  // Signing in again is the only way back. Use popup for iframes, redirect for top-level windows.
  if (isInIframe()) {
    try {
      const response = await instance.acquireTokenPopup(loginRequest);
      cacheResponse(response);
      return response.accessToken;
    } catch (popupError) {
      logger.error("Popup token acquisition failed", popupError);
      throw new Error("Failed to acquire token. Please try again.");
    }
  }

  // Come back to this page after signing in (callback.tsx reads it), not to the home page.
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath && currentPath !== "/" && currentPath !== "/callback") {
    localStorage.setItem("skyhire_redirect_after_login", currentPath);
  }
  await instance.acquireTokenRedirect(loginRequest);
  // After redirect, this code won't execute as the page will reload
  throw new Error("Redirecting to login...");
}

/**
 * Get authorization header for API calls
 */
export async function getAuthorizationHeader(): Promise<{
  "Authorization": string;
  "InstanceId": string;
  "Accept": string;
  "Content-Type": string;
}> {
  const token = await getAccessToken();
  const instanceId = getInstanceId();
  return {
    "Authorization": `Bearer ${token}`,
    "InstanceId": instanceId || "",
    "Accept": "application/json",
    "Content-Type": "application/json",
  };
}

/**
 * Get authorization header for multipart requests
 */
export async function getMultiPartAuthorizationHeader(): Promise<{
  Authorization: string;
  InstanceId: string;
  Accept: string;
}> {
  return getAuthorizationHeader();
}

/**
 * Get combined headers (authorization + content-type)
 */
export async function getHeader(): Promise<{
  "Authorization": string;
  "Content-Type": string;
  "InstanceId": string;
  "Accept": string;
}> {
  const token = await getAccessToken();
  const instanceId = getInstanceId();
  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "InstanceId": instanceId || "",
    "Accept": "application/json",
  };
}

/**
 * Get instance ID from URL query string or localStorage
 */
export function getInstanceId(): string {
  // First check URL query string
  const urlParams = new URLSearchParams(window.location.search);
  const urlInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
  if (urlInstanceId) {
    return urlInstanceId;
  }

  // Fallback to localStorage
  return localStorage.getItem(INSTANCE_STORAGE_KEY) || "";
}

/**
 * Get query string with instance ID
 */
export function getQueryString(): string {
  const instanceId = getInstanceId();
  return instanceId ? `?instanceid=${instanceId}` : "";
}

/**
 * Get saved tenant ID from localStorage
 */
export function getSavedTenantId(): string {
  return localStorage.getItem(TENANT_STORAGE_KEY) || "";
}

/**
 * Get saved instance ID from localStorage
 */
export function getSavedInstanceId(): string {
  return localStorage.getItem(INSTANCE_STORAGE_KEY) || "";
}

/**
 * Save tenant ID to localStorage
 */
export function saveTenantId(tenantId: string): void {
  if (tenantId) {
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  }
}

/**
 * Save instance ID to localStorage
 */
export function saveInstanceId(instanceId: string): void {
  if (instanceId) {
    localStorage.setItem(INSTANCE_STORAGE_KEY, instanceId);
  }
}

/**
 * Get saved theme from localStorage
 */
export function getSavedTheme(): string {
  return localStorage.getItem(THEME_STORAGE_KEY) || "Light";
}

/**
 * Save theme to localStorage
 */
export function saveTheme(theme: string): void {
  if (theme) {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }
}

/**
 * Get saved language from localStorage
 */
export function getSavedLanguage(): string {
  return localStorage.getItem(LANGUAGE_STORAGE_KEY) || "English";
}

/**
 * Save language to localStorage
 */
export function saveLanguage(language: string): void {
  if (language) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }
}

/**
 * Get current user information
 */
export function currentUser(): any {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  const account = accounts[0];
  const idTokenClaims = account.idTokenClaims as any;

  return {
    id: account.localAccountId,
    name: account.name || idTokenClaims?.name || "",
    email: account.username || idTokenClaims?.email || "",
    roles: idTokenClaims?.roles || [],
    isAccountEnabled: idTokenClaims?.isAccountEnabled !== false,
  };
}

/**
 * Clear all user preferences from storage
 * Called on logout to prevent cross-user data leakage
 */
export function clearInstanceSelection(): void {
  // Clear cached token
  clearCachedToken();

  // Drop the shared /auth/session cache. logoutRedirect() discards module state
  // via full navigation, but logoutPopup() (iframe path, see logout() below)
  // does not — without this a second user signing in within the cache TTL would
  // read the previous user's session payload.
  clearSessionCache();

  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(INSTANCE_STORAGE_KEY);
    localStorage.removeItem(TENANT_STORAGE_KEY);
    localStorage.removeItem(THEME_STORAGE_KEY);
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    localStorage.removeItem("skyhire_redirect_after_login");
  }
  sessionStorage.clear();
}

/**
 * Detect if the app is running inside an iframe
 * Returns true only when we're DEFINITELY in a cross-origin iframe
 * In ambiguous cases, returns false to prefer redirect auth (more reliable)
 */
export function isInIframe(): boolean {
  try {
    // Check if we're in an iframe
    const inIframe = window.self !== window.top;

    if (!inIframe) {
      return false;
    }

    // We're in an iframe - check if it's same-origin (a hosting provider's preview pane) or cross-origin
    // For same-origin iframes, we can still use redirect
    // For cross-origin iframes, we must use popup
    try {
      // If we can access parent's location, it's same-origin - prefer redirect
      const _parentHref = window.parent.location.href;
      // Same-origin iframe - we can use redirect
      return false;
    } catch {
      // Cross-origin iframe - must use popup
      return true;
    }
  } catch {
    // If we can't access window.top at all, assume we're NOT in an iframe
    // This is safer - redirect will work in most cases
    return false;
  }
}

/**
 * Clear ALL MSAL state from session and local storage
 * This aggressively fixes the "interaction_in_progress" error by removing
 * all MSAL-related keys, not just interaction keys
 */
function clearAllMsalState(): void {
  const clearMsalKeys = (storage: Storage) => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && (key.startsWith("msal.") || key.includes(".interaction."))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      storage.removeItem(key);
    });
  };

  if (typeof sessionStorage !== "undefined") {
    clearMsalKeys(sessionStorage);
  }
  if (typeof localStorage !== "undefined") {
    clearMsalKeys(localStorage);
  }
}

/**
 * Logout user
 * Uses popup for iframes, redirect for top-level windows
 */
export async function logout(): Promise<void> {
  const instance = getMsalInstance();

  // Clear instance selection
  clearInstanceSelection();

  // Use popup for iframes, redirect otherwise
  if (isInIframe()) {
    await instance.logoutPopup();
  } else {
    await instance.logoutRedirect();
  }
}

/**
 * Login with automatic method selection
 * - Uses popup for iframes (redirect doesn't work in iframes)
 * - Uses redirect for top-level windows (better UX)
 * - Falls back to popup if redirect fails with interaction_in_progress
 */
export async function login(): Promise<void> {
  const instance = getMsalInstance();

  // If we're in an iframe, use popup (redirect won't work)
  if (isInIframe()) {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error: unknown) {
      const authError = error as { errorCode?: string };
      if (authError?.errorCode === "interaction_in_progress") {
        logger.warn("Clearing MSAL state and retrying popup login");
        clearAllMsalState();
        await new Promise((resolve) => setTimeout(resolve, 500));
        await instance.loginPopup(loginRequest);
      } else if (
        authError?.errorCode === "popup_window_error" ||
        authError?.errorCode === "user_cancelled"
      ) {
        logger.warn("Popup blocked or cancelled by user");
        throw error;
      } else {
        throw error;
      }
    }
    return;
  }

  // For top-level windows, use redirect login
  try {
    await instance.loginRedirect(loginRequest);
  } catch (error: unknown) {
    const authError = error as { errorCode?: string };
    if (authError?.errorCode === "interaction_in_progress") {
      logger.warn(
        "Redirect login failed with interaction_in_progress, clearing state and reloading",
      );

      // Clear all MSAL state from storage
      clearAllMsalState();

      // Reload the page to create a fresh MSAL instance with no stale in-memory state.
      // The proactive cleanup in createMsalInstance() will prevent the same error on reload.
      window.location.reload();
      return;
    } else {
      throw error;
    }
  }
}

/**
 * Auth service object (for backward compatibility)
 */
export const authService = {
  setMsalInstance,
  isLoggedIn,
  isInIframe,
  getAccessToken,
  getAuthorizationHeader,
  getMultiPartAuthorizationHeader,
  getHeader,
  getInstanceId,
  getQueryString,
  getSavedTenantId,
  getSavedInstanceId,
  saveTenantId,
  saveInstanceId,
  getSavedTheme,
  saveTheme,
  getSavedLanguage,
  saveLanguage,
  currentUser,
  logout,
  login,
  clearInstanceSelection,
  clearCachedToken,

  // Getter for MSAL instance (for advanced use cases)
  get msalInstance() {
    return getMsalInstance();
  },
};
